/**
 * delete_flow tool - Delete a Power Automate flow
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";
import { ConfirmationRequiredError } from "../utils/errors.js";

export const deleteFlowInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID) to delete"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
  confirm: z
    .boolean()
    .optional()
    .default(false)
    .describe("Set to true to confirm deletion. Required to prevent accidental deletions."),
});

export type DeleteFlowInput = z.infer<typeof deleteFlowInputSchema>;

export const deleteFlowTool = {
  name: "delete_flow",
  description:
    "Delete a Power Automate flow permanently. This action cannot be undone. You must set confirm=true to proceed with deletion. Use get_flow first to verify you have the correct flow.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID) to delete",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      confirm: {
        type: "boolean",
        description: "Set to true to confirm deletion. Required to prevent accidental deletions.",
      },
    },
    required: ["flowId"],
  },
};

export async function handleDeleteFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = deleteFlowInputSchema.parse(input);

  logger.info({ flowId: parsed.flowId }, "Executing delete_flow");

  // Require confirmation
  if (!parsed.confirm) {
    // The name lookup is best-effort and must stay INSIDE the try; throwing there
    // would have the bare catch swallow it and always fall back to the id.
    let label = `flow ${parsed.flowId}`;
    try {
      const flow = await api.getFlow(parsed.flowId, parsed.environment);
      label = `"${flow.properties.displayName}"`;
    } catch {
      // unreadable flow: the id alone is enough to confirm against
    }
    throw new ConfirmationRequiredError(`Confirm delete ${label}? Set confirm=true to proceed.`);
  }

  // Get flow info before deletion for confirmation message
  let flowDisplayName = parsed.flowId;
  try {
    const flow = await api.getFlow(parsed.flowId, parsed.environment);
    flowDisplayName = flow.properties.displayName;
  } catch {
    // Flow might already be deleted or inaccessible
  }

  // Delete the flow
  await api.deleteFlow(parsed.flowId, parsed.environment);

  return `Deleted "${flowDisplayName}"`;
}
